import { useMemo } from 'react';
import { useLabObservationTrend } from '@/patients/services/use-patient-lab-results';
import {
  trendPointsInRange,
  type LabTrendCardModel,
  type LabTrendPoint,
  type TimelineRange,
} from '@/patients/lib/lab-trend-cards';

const NO_POINTS: LabTrendPoint[] = [];

/** The results of one card in the range, oldest first: shared by the card and the Compare panel (one cached read). */
export function useCardPoints(
  patientId: string,
  card: LabTrendCardModel,
  labsByRoot: ReadonlyMap<string, string>,
  range: TimelineRange,
): { points: LabTrendPoint[]; loading: boolean; failed: boolean } {
  const { data, isLoading, isError } = useLabObservationTrend(patientId, card.row.trendKey);
  const answered = !!data && data.storeAvailable !== false;
  // Same array while the inputs are the same, so a card's chart option is built once (useMemo in the card).
  const points = useMemo(
    () => (answered && data ? trendPointsInRange(data.observations, card.row, labsByRoot, range) : NO_POINTS),
    [answered, data, card.row, labsByRoot, range],
  );
  // React Query keeps the last data when a refetch fails, so the results already read still show. The card says the read
  // failed only when there is nothing to show (a failed first read, or a results store that is not answering).
  if (answered) return { points, loading: false, failed: false };
  if (isError || data?.storeAvailable === false) return { points: NO_POINTS, loading: false, failed: true };
  return { points: NO_POINTS, loading: isLoading, failed: false };
}
